import React, { useEffect, useMemo, useState } from "react";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
} from "material-react-table";
import { Button } from "@fluentui/react-components";
import { useDispatch, useSelector } from "react-redux";
import apiServices from "../../../service";
import OverlayLoader from "../../../components/OverlayLoader";
import SavedSearchDialog from "../../client/SavedSearches";
import {
  fetchExtracts,
  selectConditionExtracts,
  selectSelectionExtracts,
} from "../../../redux/savedSearchSlices/extractSlice";
import { AppDispatch } from "../../../redux/store";

// Define the type for a client row
interface ClientRow {
  customer_id: string;
  first_names: string;
  last_name: string;
  email: string;
}

function ClientSelection(props: any) {
  const dispatch = useDispatch<AppDispatch>();
  const conditionExtracts = useSelector(selectConditionExtracts);
  const selectionExtracts = useSelector(selectSelectionExtracts);

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [selectedClientsMap, setSelectedClientsMap] = useState<Map<string, ClientRow>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [savedSearchOpen, setSavedSearchOpen] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState<{
    title: string;
    conditionIds: string[];
    selectionIds: string[];
    selectionId: string | null;
  } | null>(null);

  const columns = useMemo<MRT_ColumnDef<ClientRow>[]>(
    () => [
      {
        accessorKey: "first_names",
        header: "First Name",
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
      },
    ],
    []
  );

  const searchStateKey = selectedSearch
    ? `${selectedSearch.conditionIds.join(',')}-${selectedSearch.selectionId || ''}`
    : '';

  useEffect(() => {
    getClientList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, searchText, searchStateKey]);

  useEffect(() => {
    dispatch(fetchExtracts());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function getClientList() {
    setLoading(true);
    try {
      if (selectedSearch?.selectionIds?.length) {
        // Extract by Selection: fetch specific customers by their IDs
        const res = await apiServices.exportExcel.clientList(selectedSearch.selectionIds);
        const data: any[] = res?.data?.data ?? [];
        const transformedData = data.map((item: any) => ({
  customer_id: item.CustomerId,
  first_names: item.FirstNames || "",
  last_name: item.LastName || "",
  email: item.Email || "", // Add the email property here
}));
        setClients(transformedData);
        setRowCount(res?.data?.count ?? transformedData.length);
        props.setClientList(transformedData);
      } else {
        // Extract by Condition or no filter
        const resp = await apiServices.client.getClientLists(
          pagination.pageIndex + 1,
          pagination.pageSize,
          searchText,
          undefined,
          undefined,
          selectedSearch?.conditionIds?.join(',')
        );
        if (resp?.data?.data?.customerList?.length > 0) {
          const transformedData = resp.data.data.customerList.map((client: any) => ({
            customer_id: client.customer_id,
            first_names: client?.first_names || "",
            last_name: client?.last_name || "",
          }));
          setClients(transformedData);
          setRowCount(resp.data.data.count ?? 0);
          props.setClientList(transformedData);
        } else {
          setClients([]);
          setRowCount(0);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }
  const handleSavedSearchApply = (data: {
    conditionIds: string[];
    selectionIds?: string[];
    selectionId: string | null;
    encodedCondition?: string | null;
  }) => {
    if (data.conditionIds.length > 0) {
      const extract = conditionExtracts.find((e: any) => e.SearchId === data.conditionIds[0]);
      setSelectedSearch({
        title: extract?.Title || data.conditionIds[0],
        conditionIds: data.conditionIds,
        selectionIds: [],
        selectionId: null,
      });
    } else if (data.selectionId) {
      const extract = selectionExtracts.find((e: any) => e.SearchId === data.selectionId);
      setSelectedSearch({
        title: extract?.Title || data.selectionId,
        conditionIds: [],
        selectionIds: data.selectionIds || [],
        selectionId: data.selectionId,
      });
    }
    setSavedSearchOpen(false);
  };

  // Update the accumulated map whenever the visible page or selection changes.
  // Only clients on the current page are affected; selections from other pages are preserved.
  useEffect(() => {
    setSelectedClientsMap((prev) => {
      const updated = new Map(prev);
      clients.forEach((client) => {
        if (rowSelection[client.customer_id]) {
          updated.set(client.customer_id, client);
        } else {
          updated.delete(client.customer_id);
        }
      });
      return updated;
    });
  }, [rowSelection, clients]);

  useEffect(() => {
    if (props.handleClientSelection) {
      props.handleClientSelection(Array.from(selectedClientsMap.values()));
    }
  }, [selectedClientsMap]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <OverlayLoader isLoading={loading} />

      <div className="table-in-popup">
        <MaterialReactTable<ClientRow>
          columns={columns}
          data={clients}
          onRowSelectionChange={setRowSelection}
          onPaginationChange={setPagination}
          rowCount={rowCount}
          manualPagination
          state={{ pagination, rowSelection }}
          enableGlobalFilter={true}
          enableColumnActions={false}
          enableColumnFilters={true}
          enableRowSelection
          muiTableBodyCellProps={({ column }) => ({
            style: {
              padding: "6px 12px",
              fontSize: "0.875rem",
            },
          })}
          enableFullScreenToggle={false}
          getRowId={(row) => row.customer_id}
          enableMultiRowSelection={true}
          muiTablePaginationProps={{
            showFirstButton: false,
            showLastButton: false,
          }}
          onGlobalFilterChange={(text: string) => {
            if (text) {
              setSearchText(text);
            } else {
              setSearchText("");
            }
          }}
          renderTopToolbarCustomActions={() => (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Button
                shape="rounded"
                appearance={selectedSearch ? "primary" : "outline"}
                className={selectedSearch ? "asc-button-primary" : ""}
                onClick={() => setSavedSearchOpen(true)}
              >
                {selectedSearch ? `Search: ${selectedSearch.title}` : "Saved Search"}
              </Button>
              {selectedSearch && (
                <Button
                  appearance="subtle"
                  shape="circular"
                  style={{ minWidth: "auto" }}
                  onClick={() => setSelectedSearch(null)}
                >
                  ✕
                </Button>
              )}
            </div>
          )}
        />
      </div>

      <SavedSearchDialog
        open={savedSearchOpen}
        onClose={() => setSavedSearchOpen(false)}
        onApply={handleSavedSearchApply}
        onClear={() => setSelectedSearch(null)}
        initialConditionIds={selectedSearch?.conditionIds || []}
        initialSelectionId={selectedSearch?.selectionId || null}
        singleSelect
        showAddView={false}
      />
    </div>
  );
}

export default ClientSelection;
